import { useQueryClient, UseQueryResult } from 'react-query';
import { NotificationState } from 'src/api';
import { handleErrorToast } from 'src/components/molecules';
import {
  NotificationsQuery,
  useUpdateNotificationsMutation,
} from 'src/graphql';

interface Props {
  notifications: UseQueryResult<NotificationsQuery, unknown>;
  setIsOpen: (boolean: boolean) => void;
}

export const useNotificationManagement = ({
  notifications,
  setIsOpen,
}: Props) => {
  const queryClient = useQueryClient();
  const updateNotifications = useUpdateNotificationsMutation({
    onSuccess: () => {
      queryClient.invalidateQueries('Notifications');
    },
    onError: () => {
      handleErrorToast({ toastMessage: 'Could not update notifications.' });
    },
  });

  const updateNotificationStatuses = (
    ids: number[],
    status: NotificationState,
  ) => {
    if (!ids.length) {
      return;
    }
    updateNotifications.mutate({
      notifications: {
        ids: ids,
        state: status,
      },
    });
  };

  const clearAll = async () => {
    const notificationsToClear = notifications.data?.notifications.map(
      (n) => n.id as number,
    );
    updateNotificationStatuses(
      notificationsToClear ?? [],
      NotificationState.CLOSED,
    );
    setIsOpen(true);
  };

  const handleNotificationClose = async (notificationId: number) => {
    const notificationsToClosed = [notificationId];

    updateNotificationStatuses(notificationsToClosed, NotificationState.CLOSED);
    setIsOpen(true);
  };

  const handleNotificationRead = async (notificationId: number) => {
    const notificationsToRead = [notificationId];

    updateNotificationStatuses(notificationsToRead, NotificationState.READ);
    setIsOpen(true);
  };

  return {
    clearAll,
    handleNotificationClose,
    handleNotificationRead,
    updateNotificationStatuses,
  };
};
